import type { WarehouseApiError } from '../api/warehouse.api';
import type { CsvImportError, CsvImportOrderRow, FieldError } from '../types';

/**
 * Pure helpers that turn what the outbound-order CSV import answers into
 * something explicit on screen: which order / row / column is wrong and why,
 * and — when the request itself is refused before any row is looked at — what
 * exactly was refused and what to do about it. No React here.
 */

/** CSV column → label shown next to the raw column name. */
export const CSV_COLUMN_LABEL: Readonly<Record<string, string>> = {
  external_id: 'External ID',
  recipient_name: 'Nama penerima',
  recipient_phone: 'No. HP penerima',
  destination_address: 'Alamat tujuan',
  latitude: 'Latitude',
  longitude: 'Longitude',
  destination_notes: 'Catatan tujuan',
  expected_delivery_date: 'Tanggal kirim',
  service_type: 'Service type',
  sku: 'SKU',
  qty: 'Qty',
  notes: 'Catatan',
};

export const csvColumnLabel = (field: string): string | null =>
  CSV_COLUMN_LABEL[field] ?? null;

/** Form fields the dry-run / commit request carries besides the file. */
const FORM_FIELD_LABEL: Readonly<Record<string, string>> = {
  hubID: 'Hub',
  hubName: 'Nama hub',
  hubCode: 'Kode hub',
  clientID: 'Client',
  clientName: 'Nama client',
  clientCode: 'Kode client',
  file: 'File CSV',
};

/** class-validator's English → the module's Indonesian, for the few shapes
 *  these form fields can produce. Anything else is shown as sent. */
export const humanizeValidationMessage = (message: string): string => {
  const m = message.trim();
  let r: RegExpExecArray | null;
  if ((r = /shorter than or equal to (\d+) characters/.exec(m)))
    return `Maksimal ${r[1]} karakter.`;
  if ((r = /longer than or equal to (\d+) characters/.exec(m)))
    return `Minimal ${r[1]} karakter.`;
  if (/should not be empty|must not be empty|is required/i.test(m))
    return 'Wajib diisi.';
  if (/must be an integer|must be a number/i.test(m)) return 'Harus angka.';
  if (/must be a string/i.test(m)) return 'Harus teks.';
  if (/must be a positive number/i.test(m)) return 'Harus lebih dari 0.';
  return m;
};

export type PickErrorKind = 'file' | 'form' | 'request';

/** A refusal of the whole upload (nothing row-level), ready to render. */
export interface PickError {
  kind: PickErrorKind;
  title: string;
  /** The server's / guard's own words. */
  detail: string | null;
  /** One line per refused form field (kind `form`). */
  fields: { field: string; label: string; message: string }[];
  /** What to do next. */
  hint: string | null;
}

/** A local guard refused the file before any request (type / size). */
export const fileGuardError = (message: string): PickError => ({
  kind: 'file',
  title: 'File tidak bisa dipakai',
  detail: message,
  fields: [],
  hint: 'Pakai file .csv dari "Download template", maksimal 2 MB.',
});

/**
 * The dry-run was refused before any row was looked at. `CSV_INVALID_FILE`
 * is about the file (empty, header, too many rows); `VALIDATION` is about
 * the hub / client the request carries — not the CSV — so each refused field
 * is named; anything else is the request itself (network, server).
 */
export const describePickError = (e: WarehouseApiError): PickError => {
  if (e.code === 'CSV_INVALID_FILE') {
    return {
      kind: 'file',
      title: 'File CSV tidak bisa dibaca',
      detail: e.message,
      fields: [],
      hint: 'Download template terbaru, salin data ke sana (nama kolom di baris pertama harus sama persis), lalu upload ulang.',
    };
  }
  if (e.code === 'VALIDATION' && e.fieldErrors.length) {
    return {
      kind: 'form',
      title: 'Data hub / client yang dikirim ditolak server',
      detail: null,
      fields: e.fieldErrors.map((f: FieldError) => ({
        field: f.field,
        label: FORM_FIELD_LABEL[f.field] ?? f.field,
        message: humanizeValidationMessage(f.message),
      })),
      hint: 'Ini data hub dan client yang dipilih di atas, bukan isi CSV. Coba pilih ulang hub / client; kalau tetap ditolak, data master-nya perlu dibenerin dulu.',
    };
  }
  return requestPickError(e, 'Preview gagal', 'Preview');
};

/** The request itself failed (network, server): retry, the file is fine. */
const requestPickError = (
  e: WarehouseApiError,
  title: string,
  action: string
): PickError => ({
  kind: 'request',
  title,
  detail: e.message,
  fields: [],
  hint:
    e.code === 'NETWORK'
      ? `Periksa koneksi lalu klik ${action} lagi.`
      : e.status !== null && e.status >= 500
        ? 'Server sedang bermasalah. Coba lagi sebentar lagi; file-nya tidak perlu diubah.'
        : null,
});

/**
 * A refusal of an upload that is not the outbound dry-run (capacity, count
 * sheet): the file itself (`CSV_INVALID_FILE`, or a `VALIDATION` that only
 * names `file`) gets `fileHint` — what to do — and anything else is shown as
 * the request failing under `requestTitle`.
 */
export const describeUploadError = (
  e: WarehouseApiError,
  opts: { fileHint: string; requestTitle: string; requestAction: string }
): PickError => {
  const fields = e.fieldErrors;
  const aboutFile =
    e.code === 'CSV_INVALID_FILE' ||
    (e.code === 'VALIDATION' &&
      fields.length > 0 &&
      fields.every((f) => f.field === 'file'));
  if (aboutFile) {
    return {
      kind: 'file',
      title: 'File CSV tidak bisa dipakai',
      detail:
        e.code === 'CSV_INVALID_FILE'
          ? e.message
          : fields.map((f) => f.message).join(' '),
      fields: [],
      hint: opts.fileHint,
    };
  }
  if (e.code === 'VALIDATION' && fields.length) {
    return {
      kind: 'form',
      title: 'Data yang dikirim ditolak server',
      detail: null,
      fields: fields.map((f: FieldError) => ({
        field: f.field,
        label: FORM_FIELD_LABEL[f.field] ?? f.field,
        message: humanizeValidationMessage(f.message),
      })),
      hint: 'Ini isian di luar isi CSV (pilihan hub / client di atas). Pilih ulang; kalau tetap ditolak, data master-nya perlu dibenerin dulu.',
    };
  }
  return requestPickError(e, opts.requestTitle, opts.requestAction);
};

/** A problem found in the browser before anything is sent (file, header, size). */
export const localFileProblem = (
  detail: string,
  hint: string | null = null,
  title = 'File tidak bisa dipakai'
): PickError => ({ kind: 'file', title, detail, fields: [], hint });

/* ------------------------------- row errors ------------------------------ */

/**
 * `Baris N: pesan` strings (what older servers answer) as error rows: the
 * whole row is named, since the column is not known.
 */
export const messagesToErrors = (
  messages: readonly string[]
): CsvImportError[] =>
  messages.map((m, i) => {
    const r = /^Baris\s+(\d+):\s*([\s\S]*)$/.exec(m);
    return {
      row: r ? Number(r[1]) : i + 1,
      field: 'row',
      value: '',
      message: r ? r[2] : m,
    };
  });

/** Rows of a plain error CSV download: row, column, value, reason. */
export const plainErrorCsvRows = (
  errors: readonly CsvImportError[]
): (string | number)[][] => [
  ['row', 'field', 'value', 'reason'],
  ...errors.map((e) => [e.row, e.field, e.value, e.message]),
];

/** The order (external_id) a CSV row belongs to, if the preview lists it. */
export const orderOfRow = (
  orders: readonly CsvImportOrderRow[],
  row: number
): CsvImportOrderRow | null => orders.find((o) => o.rows.includes(row)) ?? null;

/** Row errors of one order, in file order. */
export const errorsOfOrder = (
  errors: readonly CsvImportError[],
  order: CsvImportOrderRow
): CsvImportError[] => {
  const rows = new Set(order.rows);
  return errors.filter((e) => rows.has(e.row));
};

/** Distinct columns an order's errors name, in file order. */
export const errorColumnsOf = (
  errors: readonly CsvImportError[],
  order: CsvImportOrderRow
): string[] =>
  Array.from(new Set(errorsOfOrder(errors, order).map((e) => e.field)));

/** Rows of the error CSV download: order, row, column, value, reason. */
export const errorCsvRows = (
  errors: readonly CsvImportError[],
  orders: readonly CsvImportOrderRow[]
): (string | number)[][] => [
  ['external_id', 'row', 'field', 'value', 'reason'],
  ...errors.map((e) => [
    orderOfRow(orders, e.row)?.externalID ?? '',
    e.row,
    e.field,
    e.value,
    e.message,
  ]),
];

export type PreviewTab = 'ALL' | 'ERROR' | 'NEW' | 'EXISTS';

/** Where the preview opens: on the errors when there are any. */
export const defaultPreviewTab = (errorCount: number): PreviewTab =>
  errorCount > 0 ? 'ERROR' : 'ALL';

export const ordersOfTab = (
  orders: readonly CsvImportOrderRow[],
  tab: PreviewTab
): CsvImportOrderRow[] =>
  tab === 'ALL' ? [...orders] : orders.filter((o) => o.status === tab);
